import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Shield, HelpCircle, Info, Calculator } from 'lucide-react'

interface MiscellaneousStepProps {
  onNext: () => void
  onSkip: () => void
}

export function MiscellaneousStep({ onNext, onSkip }: MiscellaneousStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses } = useBudgetAssistantStore()
  
  const [emergencyFundPercentage, setEmergencyFundPercentage] = useState('')
  const [miscExpenses, setMiscExpenses] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Load existing responses into local state
  useEffect(() => {
    if (responses.emergencyFundPercentage) {
      setEmergencyFundPercentage(responses.emergencyFundPercentage.toString())
    }
    if (responses.miscExpenses) {
      setMiscExpenses(responses.miscExpenses.toString())
    }
  }, [responses])

  const handleNext = async () => {
    if (user?.id) {
      setIsLoading(true)
      try {
        const miscData = {
          emergencyFundPercentage: emergencyFundPercentage ? parseFloat(emergencyFundPercentage) : undefined,
          miscExpenses: miscExpenses ? parseFloat(miscExpenses) : undefined
        }
        
        await updateResponses(miscData, 'misc')
        onNext()
      } catch (error) {
        console.error('Error saving miscellaneous data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-14 h-14 mb-3">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl p-3 shadow-lg">
            <Calculator className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.misc.title', 'Just a few more details')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
          {t('budgetAssistant.misc.subtitle', 'These final questions will help us create the perfect budget for you.')}
        </p>
      </div>

      <div className="space-y-5">
        {/* Emergency Fund */}
        <Card className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/30">
              <Shield className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label className="text-sm font-medium">
                  {t('budgetAssistant.misc.emergencyFund.label', 'Emergency Fund Percentage')}
                </Label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t('budgetAssistant.misc.emergencyFund.description', 'What percentage of income would you like to set aside for emergencies?')}
                </p>
              </div>
              <div className="space-y-3">
                <Select value={emergencyFundPercentage} onValueChange={setEmergencyFundPercentage}>
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder={t('budgetAssistant.misc.emergencyFund.placeholder', 'Select percentage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('budgetAssistant.misc.emergencyFund.options.none', '0% - No emergency fund')}</SelectItem>
                    <SelectItem value="5">{t('budgetAssistant.misc.emergencyFund.options.low', '5% - Basic protection')}</SelectItem>
                    <SelectItem value="10">{t('budgetAssistant.misc.emergencyFund.options.medium', '10% - Good safety net')}</SelectItem>
                    <SelectItem value="15">{t('budgetAssistant.misc.emergencyFund.options.high', '15% - Strong protection')}</SelectItem>
                    <SelectItem value="20">{t('budgetAssistant.misc.emergencyFund.options.maximum', '20% - Maximum security')}</SelectItem>
                  </SelectContent>
                </Select>
                
                {emergencyFundPercentage && emergencyFundPercentage !== '0' && responses.monthlyIncome && (
                  <div className="p-3 bg-orange-50/50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="text-center">
                      <div className="font-semibold text-orange-800 dark:text-orange-200 text-sm">
                        ${((responses.monthlyIncome * parseInt(emergencyFundPercentage)) / 100).toLocaleString()}
                      </div>
                      <div className="text-orange-600 dark:text-orange-400 text-xs">
                        {t('budgetAssistant.misc.emergencyFund.monthlyAmount', 'per month to emergency fund')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Miscellaneous Expenses */}
        <Card className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <HelpCircle className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label htmlFor="misc" className="text-sm font-medium">
                  {t('budgetAssistant.misc.miscExpenses.label', 'Other Monthly Expenses')}
                </Label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t('budgetAssistant.misc.miscExpenses.description', 'Any other regular expenses not covered above?')}
                </p>
              </div>
              <div className="relative max-w-xs">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </div>
                <Input
                  id="misc"
                  type="number"
                  placeholder="0.00"
                  value={miscExpenses}
                  onChange={(e) => setMiscExpenses(e.target.value)}
                  className="pl-8 h-10 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>
              <p className="text-xs text-gray-500">
                {t('budgetAssistant.misc.miscExpenses.examples', 'Examples: Pet care, charitable donations, professional fees')}
              </p>
            </div>
          </div>
        </Card>

        {/* Budget Summary Preview */}
        {responses.monthlyIncome && (
          <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
            <div className="space-y-3">
              <h4 className="font-medium text-emerald-900 dark:text-emerald-100 text-sm flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                {t('budgetAssistant.misc.preview.title', 'Budget Preview')}
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-emerald-800 dark:text-emerald-200 font-medium text-sm">
                    ${responses.monthlyIncome.toLocaleString()}
                  </div>
                  <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                    {t('budgetAssistant.misc.preview.income', 'Monthly Income')}
                  </div>
                </div>
                <div>
                  <div className="text-emerald-800 dark:text-emerald-200 font-medium text-sm">
                    {emergencyFundPercentage || '0'}%
                  </div>
                  <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                    {t('budgetAssistant.misc.preview.emergency', 'Emergency Fund')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Help Tips */}
        <Card className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1 text-sm">
                {t('budgetAssistant.misc.tips.title', 'Emergency Fund Tips')}
              </h4>
              <div className="space-y-1">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.misc.tips.tip1', 'Experts recommend 3-6 months of expenses')}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.misc.tips.tip2', 'Start with 5-10% if you\'re new to saving')}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.misc.tips.tip3', 'Keep emergency funds in a separate, easily accessible account')}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onSkip}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 h-10"
        >
          {t('common.skip', 'Skip')}
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={isLoading}
          className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-6 h-10"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            t('common.continue', 'Continue')
          )}
        </Button>
      </div>
    </div>
  )
} 